import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from './Button';

interface ConfirmDialogProps {
  open: boolean;
  body: string;
  confirmLabel: string;
  loading?: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Accessible confirmation modal (replaces window.confirm, which blocks the
 * renderer and can't be styled/tested). Esc or backdrop click cancels; the
 * confirm button is focused on open.
 */
export function ConfirmDialog({
  open,
  body,
  confirmLabel,
  loading = false,
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    // Backdrop dismissal only; Esc and the focused Cancel/Confirm buttons provide
    // the keyboard paths, so these element-interaction rules don't apply here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={body}>
        <p>{body}</p>
        <div className="dialog__actions">
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            ref={confirmRef}
            variant={danger ? 'danger' : 'primary'}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
